// FLEXX Session Compass — a mirror, not a gatekeeper.
//
// Philosophy: every coach has their own session edge. Coach Herman thrives in
// the Asian session. Coach X loves NY-only fade plays. We never tell traders
// when to trade — we just hold up a mirror of THEIR personal data for the
// session they're about to log a SuPER plan in.
//
// • If the trader has fewer than COMPASS_MIN_TRADES (3) closed trades in the
//   selected session → render nothing. Don't pester new traders.
// • If the trader has data → show personal session stats with one of three tones:
//     EDGE     — clear winning record (≥50% WR, ≥50% A-grade rate) → green ✅
//     NEUTRAL  — mixed performance → gold 📊 (no judgment)
//     CAUTION  — clear losing record (≤30% WR or ≤20% A-grade rate) → red ⚠️
//   …and even in CAUTION, we don't block. Just reflect.
// • Per-session "don't show again" toggle stored in localStorage.
//
// AXEL adapts: the system observes, the trader discovers, AXEL reflects.

import React, { useState, useMemo } from "react";
import Portal from "./Portal";
import { THEME } from "./state";

const COMPASS_MIN_TRADES = 3;            // below this, stay silent — let them build data
const STRONG_EDGE_WR = 50;               // % win-rate threshold for EDGE classification
const STRONG_EDGE_A = 50;                // % A-grade threshold for EDGE classification
const POOR_RESULTS_WR = 30;              // % win-rate threshold for CAUTION
const POOR_RESULTS_A = 20;               // % A-grade threshold for CAUTION
const DISMISS_KEY = "flexx_session_compass_dismissed"; // { "Asian": true, ... }

// Compute personal session stats from the user's own closed trades.
function computeSessionStats(entries, session) {
  const closed = entries.filter((e) => e.session === session && !e.isOpen);
  const decided = closed.filter((e) => e.result !== "Break Even");
  const wins = decided.filter((e) => e.result === "Win");
  const aGrade = closed.filter((e) => e.grade === "A");
  const winRate = decided.length ? Math.round((wins.length / decided.length) * 100) : 0;
  const aGradeRate = closed.length ? Math.round((aGrade.length / closed.length) * 100) : 0;
  const avgR = wins.length ? (wins.reduce((a, e) => a + (e.rr || 0), 0) / wins.length).toFixed(2) : "0.00";
  return {
    trades: closed.length,
    wins: wins.length,
    losses: decided.length - wins.length,
    winRate,
    aGradeRate,
    avgR,
  };
}

function classify(stats) {
  if (stats.winRate >= STRONG_EDGE_WR && stats.aGradeRate >= STRONG_EDGE_A) return "EDGE";
  if (stats.winRate <= POOR_RESULTS_WR || stats.aGradeRate <= POOR_RESULTS_A) return "CAUTION";
  return "NEUTRAL";
}

function isDismissed(session) {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}")[session] === true; }
  catch { return false; }
}
function markDismissed(session) {
  try {
    const map = JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}");
    map[session] = true;
    localStorage.setItem(DISMISS_KEY, JSON.stringify(map));
  } catch (_) { /* localStorage may be disabled */ }
}

/**
 * Decide whether to show the compass at all for a given save attempt.
 * Returns the precomputed stats + tone if it should show, or null.
 */
export function evaluateCompass(session, entries) {
  if (!session) return null;
  if (isDismissed(session)) return null;
  const stats = computeSessionStats(entries, session);
  if (stats.trades < COMPASS_MIN_TRADES) return null;
  const tone = classify(stats);
  return { session, stats, tone };
}

const TONE_COLORS = {
  EDGE: { primary: "#22C55E", icon: "✅", label: "YOUR EDGE" },
  NEUTRAL: { primary: THEME.gold, icon: "📊", label: "YOUR DATA" },
  CAUTION: { primary: "#FF6B35", icon: "⚠️", label: "YOUR DATA" },
};

const TONE_HEADLINES = {
  EDGE: (s) => `You're ${s.wins}-${s.losses} in this session (${s.winRate}% WR, ${s.aGradeRate}% A-grade). Trust your work.`,
  NEUTRAL: (s) => `You're ${s.wins}-${s.losses} in this session (${s.winRate}% WR, ${s.aGradeRate}% A-grade). Mixed bag — keep tracking.`,
  CAUTION: (s) => `You're ${s.wins}-${s.losses} in this session (${s.winRate}% WR, ${s.aGradeRate}% A-grade). 100% your call — just consider whether this setup is different from those losses.`,
};

const TONE_FOOTERS = {
  EDGE: "Every trader builds their edge in different sessions. This is yours. Pull the trigger.",
  NEUTRAL: "Some traders thrive in this session, others don't. Your sample's growing — keep the discipline tight and let the data speak.",
  CAUTION: "Sometimes the right move is a price-alert instead of a trade. Sometimes it's not. Your call.",
};

export default function SessionCompass({ session, stats, tone, onProceed, onHold, onClose }) {
  const [muteAfter, setMuteAfter] = useState(false);
  const c = TONE_COLORS[tone];

  const proceed = () => {
    if (muteAfter) markDismissed(session);
    onProceed?.();
  };
  const hold = () => {
    if (muteAfter) markDismissed(session);
    onHold?.();
  };

  return (
    <Portal>
      <div data-testid="session-compass-modal" style={modalBg}>
        <div style={{ ...modalCard, borderColor: `${c.primary}55`, boxShadow: `0 0 60px ${c.primary}30` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: "monospace", color: c.primary, fontSize: 10, letterSpacing: 2 }}>
                SESSION COMPASS · {c.label}
              </div>
              <h2 style={{ margin: "6px 0 0", color: THEME.text, fontSize: 22, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 0.5 }}>
                {c.icon} {session.toUpperCase()} SESSION
              </h2>
            </div>
            <button data-testid="session-compass-close" onClick={onClose}
              style={ghostBtn}>Close</button>
          </div>

          {/* Personal-data headline */}
          <div data-testid="session-compass-headline" style={{
            padding: 14, marginBottom: 14, borderRadius: 8,
            background: `${c.primary}10`,
            border: `1px solid ${c.primary}40`,
            borderLeft: `3px solid ${c.primary}`,
            color: THEME.text, fontSize: 14, lineHeight: 1.6,
          }}>
            {TONE_HEADLINES[tone](stats)}
          </div>

          {/* Stat tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8, marginBottom: 14 }}>
            <Tile label="Trades" value={stats.trades} accent={THEME.gold} />
            <Tile label="W–L" value={`${stats.wins}–${stats.losses}`} accent={THEME.text} />
            <Tile label="Win %" value={`${stats.winRate}%`} accent={stats.winRate >= 50 ? "#22C55E" : stats.winRate >= 35 ? THEME.gold : "#FF6B35"} />
            <Tile label="Avg R" value={`1:${stats.avgR}`} accent={THEME.text} />
            <Tile label="A-grade %" value={`${stats.aGradeRate}%`} accent={stats.aGradeRate >= 50 ? "#22C55E" : stats.aGradeRate >= 25 ? THEME.gold : "#FF6B35"} />
          </div>

          {/* AXEL footer reflection (no judgment, no prescription) */}
          <div style={{
            padding: 12, marginBottom: 14, borderRadius: 8,
            background: `${THEME.gold}10`, border: `1px solid ${THEME.gold}40`, borderLeft: `3px solid ${THEME.gold}`,
            color: THEME.text, fontSize: 12, lineHeight: 1.6, fontStyle: "italic",
          }}>
            <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 9, letterSpacing: 2, marginBottom: 6, fontStyle: "normal" }}>
              AXEL — REFLECTION
            </div>
            {TONE_FOOTERS[tone]}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button data-testid="session-compass-proceed" onClick={proceed}
              style={{
                ...primaryBtn, flex: "1 1 240px", padding: "12px 18px",
                background: c.primary, color: "#0a0a0a", borderColor: c.primary,
              }}>
              {tone === "EDGE" ? "PULL THE TRIGGER →" : "PROCEED — IT'S MY CALL →"}
            </button>
            <button data-testid="session-compass-hold" onClick={hold}
              style={{ ...ghostBtn, flex: "1 1 200px", padding: "12px 18px" }}>
              Set price alert instead
            </button>
          </div>

          {/* Mute toggle */}
          <label data-testid="session-compass-mute" style={{
            display: "flex", alignItems: "center", gap: 8, marginTop: 14,
            color: THEME.mute, fontSize: 11, fontFamily: "monospace", letterSpacing: 0.3, cursor: "pointer",
          }}>
            <input type="checkbox" checked={muteAfter}
              onChange={(e) => setMuteAfter(e.target.checked)}
              style={{ accentColor: THEME.gold }} />
            Don't show this for {session} again (you can re-enable in Settings)
          </label>
        </div>
      </div>
    </Portal>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div style={{
      padding: 10, borderRadius: 8, border: `1px solid ${THEME.border}`,
      background: "#0a0a0a", textAlign: "center",
    }}>
      <div style={{ fontSize: 9, letterSpacing: 1.5, color: THEME.mute, fontFamily: "monospace" }}>
        {label.toUpperCase()}
      </div>
      <div style={{ marginTop: 4, fontSize: 17, fontWeight: 800, color: accent, fontFamily: "JetBrains Mono, monospace" }}>
        {value}
      </div>
    </div>
  );
}

const modalBg = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)",
  zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
};
const modalCard = {
  background: THEME.surface, border: `1px solid ${THEME.border}`,
  borderRadius: 12, padding: 22, width: "min(560px, 100%)", maxHeight: "92vh", overflowY: "auto",
};
const primaryBtn = {
  background: THEME.gold, color: "#0a0a0a", border: `1px solid ${THEME.gold}`,
  borderRadius: 8, fontWeight: 800, fontSize: 13, fontFamily: "monospace", letterSpacing: 1, cursor: "pointer",
};
const ghostBtn = {
  background: "transparent", color: THEME.mute, border: `1px solid ${THEME.border}`,
  borderRadius: 8, fontWeight: 700, fontSize: 12, fontFamily: "monospace", letterSpacing: 1, cursor: "pointer",
  padding: "6px 12px",
};
